/**
 * /supplementary-payment — 补差价支付页
 *
 * 设计稿: _design_reference/mini-supplementary-payment.html
 *
 * 从 ?orderNo= 读取订单号 → useOrderDetail 拉真实数据
 * → 选择「同意补款」→ MiniCashier 收银台(完整渠道列表 + 轮询)
 * → 选择「取消订单」→ useCancelOrder → /orders
 *
 * 差额明细:后端 OrderDetail 无专属 diff 字段,不展示伪造数据。
 * 倒计时:OrderDetail 无 expiredTime 字段,不展示倒计时。
 * 买手留言:只在 order.remarks 有值时展示。
 */
import { useCancelOrder, useOrderDetail } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MiniCashier } from "../components/pay/MiniCashier";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuthToken } from "../lib/auth";

// ---------------------------------------------------------------------------
// Read ?orderNo= from URL search string
// ---------------------------------------------------------------------------
function getParam(search: string, key: string): string {
  const u = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return u.get(key) ?? "";
}

// ---------------------------------------------------------------------------
// Choice type — partial 已删除(无数据源)
// ---------------------------------------------------------------------------
type Choice = "accept" | "cancel";

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export function SupplementaryPaymentPage() {
  const { t } = useTranslation("supplementary_payment");
  const authToken = useAuthToken();
  const navigate = useNavigate();

  // Read ?orderNo= from URL
  const searchStr = useRouterState({ select: (s) => s.location.searchStr ?? "" });
  const orderNo = getParam(searchStr, "orderNo");

  // Real order data
  const orderQ = useOrderDetail(authToken, orderNo);
  const order = orderQ.data;

  // Cancel mutation
  const cancelOrder = useCancelOrder(authToken);

  // User choice
  const [choice, setChoice] = useState<Choice>("accept");

  // After "accept" is confirmed, switch to cashier view
  const [showCashier, setShowCashier] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Real supplementary amount from order (totalPayAmountRubKopecks = remaining
  // amount the user owes; falls back to orderAmountRubKopecks when unavailable)
  const supplementKopecks =
    order?.totalPayAmountRubKopecks ?? order?.orderAmountRubKopecks ?? 0;

  // Refund amount = what was originally paid
  const refundKopecks = order?.totalPayAmountRubKopecks ?? order?.orderAmountRubKopecks ?? 0;

  // ---------------------------------------------------------------------------
  // If "accept" was chosen and user tapped submit → show MiniCashier
  // ---------------------------------------------------------------------------
  if (showCashier && authToken && orderNo) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.order_prefix", { orderNo })} back />
        <div className="tg-body" style={{ flex: 1 }}>
          {/* Dark receipt summary */}
          <div
            className="tg-card"
            style={{
              background: "linear-gradient(135deg,var(--color-text),#1A2924)",
              color: "#fff",
              border: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                paddingTop: 8,
                marginTop: 6,
                borderTop: "2px solid var(--color-sticker-yellow)",
              }}
            >
              <div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,.65)" }}>
                  {t("hero.need", { amount: "" })}
                </div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,.5)", fontFamily: "monospace" }}>
                  {orderNo}
                </div>
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "var(--color-sticker-yellow)",
                }}
              >
                {supplementKopecks > 0
                  ? formatPrice(supplementKopecks, "ru-RU", "RUB")
                  : "—"}
              </div>
            </div>
          </div>

          <MiniCashier orderNo={orderNo} authToken={authToken} />
        </div>
      </TgPage>
    );
  }

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------
  async function handleSubmit() {
    setError(null);
    if (!authToken) return;

    if (choice === "accept") {
      // Switch to MiniCashier view
      setShowCashier(true);
      return;
    }

    if (choice === "cancel") {
      try {
        await cancelOrder.mutateAsync({
          orderNo,
          cancelReason: t("cancel_reason"),
        });
        navigate({ to: "/orders" as never });
      } catch {
        setError(t("error_cancel"));
      }
    }
  }

  const isPending = cancelOrder.isPending;

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  if (orderQ.isLoading) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={orderNo ? t("head.order_prefix", { orderNo }) : t("head.title")} back />
        <div className="tg-body" style={{ flex: 1 }}>
          <TgSkeleton height={160} />
        </div>
      </TgPage>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const displayOrderNo = order?.orderNo ?? orderNo;

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.order_prefix", { orderNo: displayOrderNo })} back />

      {/* Hero banner */}
      <div
        style={{
          background: "linear-gradient(135deg,#FFF8DA,#FFE5A0)",
          borderBottom: "1px solid var(--color-sticker-yellow)",
          padding: 16,
          display: "flex",
          gap: 12,
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: "var(--color-sticker-yellow)",
            color: "var(--color-sticker-yellow-fg)",
            display: "grid",
            placeItems: "center",
            fontSize: 20,
          }}
        >
          {t("hero.badge")}
        </div>
        <div style={{ flex: 1 }}>
          <strong
            style={{
              fontSize: 13,
              color: "var(--color-sticker-yellow-fg)",
              display: "block",
            }}
          >
            {t("hero.need", { amount: "" })}
            <span
              style={{
                color: "var(--color-cta)",
                fontFamily: "-apple-system,system-ui,sans-serif",
                fontSize: 18,
              }}
            >
              {supplementKopecks > 0
                ? formatPrice(supplementKopecks, "ru-RU", "RUB")
                : "—"}
            </span>
          </strong>
          {/* deadline text only — no countdown, expiredTime not in OrderDetail */}
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>
            {t("hero.deadline")}
          </div>
        </div>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* Options */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "10px 12px",
              background: "var(--color-surface)",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {t("diff.options_title")}
          </div>

          {/* Accept all */}
          <label
            style={{
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              gap: 8,
              alignItems: "center",
              background: choice === "accept" ? "var(--color-primary-soft)" : undefined,
              borderTop: "1px solid var(--color-border)",
              cursor: "pointer",
            }}
            onClick={() => setChoice("accept")}
          >
            <div>
              <strong style={{ fontSize: 11 }}>{t("diff.accept_all")}</strong>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                {t("diff.accept_all_desc")}
              </div>
            </div>
            <span
              style={{
                fontFamily: "-apple-system,system-ui,sans-serif",
                fontSize: 13,
                fontWeight: 900,
                color: "var(--color-cta)",
              }}
            >
              {supplementKopecks > 0
                ? `+ ${formatPrice(supplementKopecks, "ru-RU", "RUB")}`
                : "—"}
            </span>
            <Radio active={choice === "accept"} />
          </label>

          {/* Cancel */}
          <label
            style={{
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
              alignItems: "center",
              borderTop: "1px solid var(--color-border)",
              cursor: "pointer",
            }}
            onClick={() => setChoice("cancel")}
          >
            <div>
              <strong style={{ fontSize: 11, color: "var(--color-danger)" }}>
                {t("diff.cancel")}
              </strong>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                {refundKopecks > 0
                  ? t("diff.cancel_desc", {
                      refund: Math.round(refundKopecks / 100).toLocaleString("ru-RU"),
                    })
                  : t("diff.cancel_desc_generic")}
              </div>
            </div>
            <Radio active={choice === "cancel"} />
          </label>
        </div>

        {/* Buyer remarks — only when order.remarks has a real value */}
        {order?.remarks && (
          <div className="tg-card">
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                  {t("remarks_title")}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: "var(--color-text)",
                  }}
                >
                  {order.remarks}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              margin: "0 16px",
              padding: "10px 14px",
              background: "#FFF0EB",
              border: "1px solid var(--color-cta)",
              borderRadius: 8,
              fontSize: 11,
              color: "var(--color-cta)",
            }}
          >
            {error}
          </div>
        )}
      </div>

      <TgMain disabled={isPending} onClick={handleSubmit}>
        {isPending
          ? t("cancelling")
          : choice === "cancel"
            ? t("cancel_cta")
            : t("submit", {
                amount:
                  supplementKopecks > 0
                    ? formatPrice(supplementKopecks, "ru-RU", "RUB")
                    : "—",
              })}
      </TgMain>
    </TgPage>
  );
}

// ---------------------------------------------------------------------------
// Radio indicator
// ---------------------------------------------------------------------------
function Radio({ active }: { active: boolean }) {
  return (
    <div
      style={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: `2px solid ${active ? "var(--color-primary-fill)" : "var(--color-border-strong)"}`,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {active && (
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "var(--color-primary-fill)",
          }}
        />
      )}
    </div>
  );
}
